import styled, { css } from 'styled-components';
import React from 'react';
import { UpdateWorkspace } from '../../../../shared/types/workspaceTypes';
import { FlexRow } from '../../../../shared/FlexRow';
import { Body, Headline, Label, Title } from '../../../../shared/styled-components/styled_text';
import { FlexCol } from '../../../../shared/FlexCol';
import { GuidePreview } from '../GuidePreview';

interface Props {
  data: UpdateWorkspace;
}

export const OnboardingStep1Preview: React.FC<Props> = ({ data }) => {
  return (
    <Container>
      <FlexRow
        width={'100%'}
        justify={'space-between'}
        align={'flex-start'}
        style={{ padding: '0 1.2rem 1.6rem', borderBottom: '1px solid var(--border-color)' }}
      >
        <Headline size={'m'}>미리보기</Headline>
        <Body size={'m'} style={{ color: ' var(--content-color-secondary)' }}>
          지원자에게 발송하는 안내 메시지(왼: 알림톡, 오: 안내 페이지)
        </Body>
      </FlexRow>
      <ContentWrapper>
        <PhoneContainer $background={'left'}>
          <PhoneContentLeft>
            <FlexCol width={'100%'} align={'flex-start'} gap={'0.8rem'}>
              <FlexCol
                width={'100%'}
                align={'flex-start'}
                style={{
                  paddingBottom: '1.2rem',
                  borderBottom: '1px solid var(--border-color)',
                  boxSizing: 'border-box',
                }}
              >
                <Label size={'s'}>(알림)</Label>
                <FlexRow width={'100%'} justify={'flex-start'} align={'center'} gap={'0.2rem'} style={{ boxSizing: 'border-box' }}>
                  <CompanyName $type={'title'} $filled={!!data.name}>
                    {!data.name ? '회사 이름' : data.name}
                  </CompanyName>
                  <Title size={'s'}>안내</Title>
                </FlexRow>
              </FlexCol>
              <FlexCol width={'100%'} align={'flex-start'} gap={'0.8rem'}>
                <FlexCol width={'100%'} align={'flex-start'} gap={'0.4rem'}>
                  <FlexRow width={'fit-content'} justify={'flex-start'} align={'center'}>
                    <CompanyName $type={'content'} style={{ paddingRight: '0' }} $filled={!!data.name}>
                      {'['}
                    </CompanyName>
                    <CompanyName $type={'subtitle'} $filled={!!data.name}>
                      {!data.name ? '회사 이름' : data.name}
                    </CompanyName>
                    <CompanyName $type={'content'} style={{ paddingLeft: '0' }} $filled={!!data.name}>
                      {']'}
                    </CompanyName>
                  </FlexRow>
                  <FlexRow width={'100%'} justify={'flex-start'} align={'center'} gap={'0.2rem'}>
                    <Body size={'s'}>지원하신</Body>
                    <CompanyName $type={'content'} $filled={!!data.name}>
                      {!data.name ? '회사 이름' : data.name}
                    </CompanyName>
                    <Body size={'s'}>에서</Body>
                  </FlexRow>
                  <Body size={'s'} style={{ marginTop: '-0.2rem' }}>
                    알림이 도착했습니다.
                  </Body>
                </FlexCol>
                <FlexCol width={'100%'} align={'flex-start'} gap={'0.2rem'}>
                  <Body size={'s'}>변동사항 발생 시 연락해주세요.</Body>
                  <FlexRow width={'100%'} justify={'flex-start'} align={'center'} gap={'0.2rem'}>
                    <Body size={'s'}>연락처:</Body>
                    <CompanyName $type={'content'} $filled={!!data.contact.officer_contact}>
                      {!data.contact.officer_contact ? '회사 연락처' : data.contact.officer_contact}
                    </CompanyName>
                  </FlexRow>
                </FlexCol>
              </FlexCol>
            </FlexCol>
            <FlexRow
              width={'100%'}
              justify={'center'}
              align={'center'}
              gap={'0.8rem'}
              style={{
                paddingBlock: '0.6rem',
                backgroundColor: 'var(--background-2)',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              <Label size={'s'}>내게 온 알림 확인하기</Label>
            </FlexRow>
          </PhoneContentLeft>
        </PhoneContainer>
        <PhoneContainer $background={'right'}>
          <RadiusBox>
            <GuidePreview data={data} />
          </RadiusBox>
        </PhoneContainer>
      </ContentWrapper>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  min-width: 86.2rem;
  min-height: 87.5rem;
  padding: 2.4rem 3.2rem;
  border-radius: 24px;
  background: var(--background-2);
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2.4rem;
`;

const ContentWrapper = styled.div`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: 4.8rem;
`;

const PhoneContainer = styled.div<{ $background: string }>`
  width: 37.5rem;
  height: 75rem;
  background-image: url(${props => `/images/workspace-wizard/onboarding_step1_preview_${props.$background}.png`});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  position: relative;
`;

const PhoneContentLeft = styled.div`
  width: 26.13rem;
  height: fit-content;
  max-height: 45rem;
  padding: 1.2rem;
  background: var(--background-1);
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  box-sizing: border-box;

  position: absolute;
  top: 18.46rem;
  left: 6.32rem;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.6rem;
`;

const CompanyName = styled.div<{ $type: 'title' | 'content' | 'subtitle'; $filled?: boolean }>`
  width: fit-content;
  height: fit-content;
  background: var(--mono-op-12);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  color: var(--content-color-${props => (props.$filled ? 'primary' : 'tertiary')});

  ${props =>
    props.$type !== 'subtitle' &&
    css`
      padding-inline: 0.4rem;
      border-radius: 4px;
    `}

  ${props =>
    props.$type === 'title'
      ? css`
          max-width: 16rem;
          font-size: 1.6rem;
          font-weight: 600;
          line-height: 150%;
        `
      : css`
          max-width: 12rem;
          font-size: 1.4rem;
          font-weight: 500;
          line-height: 160%;
        `}
`;

const RadiusBox = styled.div`
  width: 33.3rem;
  height: 60.2rem;
  max-height: 60.2rem;
  border-bottom-left-radius: 44px;
  border-bottom-right-radius: 44px;

  position: absolute;
  top: 12rem;
  left: 1.98rem;
`;
